import type {
  ConfirmOrderChildPlan,
  ConfirmOrderMainPlan,
  DeliveryInfo,
  HalebopConfirmOrderRequest,
  MsisdnOption,
} from '~/models/checkout/ConfirmOrderRequest.api.model';
import type { UserState, FetchedData } from '@/stores/checkout.store';
import type {
  KeepNumberOptions,
  NumberConfigState,
} from '~/models/checkout/checkout_store/NumberConfigCheckoutState.model';
import type { CustomerConsentDto } from '~/models/customer/CustomerConsent.api.model';
import { INSTABOX_SYSTEM_NAME } from '~/repository/modules/ShippingMethod.module';

const mapNumberConfigKeepNumberOption = (
  option?: KeepNumberOptions,
): MsisdnOption => {
  switch (option) {
    case 'keep-number':
      return 'KeepNumber';
    case 'keep-number-other':
      return 'KeepNumberTransferOwner';
    default:
      return 'NewNumber';
  }
};

const _shouldIncludeCurrentPhoneNumber = (
  MsisdnOption: MsisdnOption,
): boolean => {
  return (
    MsisdnOption === 'KeepNumber' || MsisdnOption === 'KeepNumberTransferOwner'
  );
};

const _determineOwnerSocialSecurityNumber = (
  MsisdnOption: MsisdnOption,
  numberConfig: NumberConfigState,
): string | undefined => {
  if (
    MsisdnOption === 'KeepNumberTransferOwner' &&
    numberConfig.ownerSocialSecurityNumber
  ) {
    return numberConfig.ownerSocialSecurityNumber;
  }
  return undefined;
};

const _createChildPlanData = (numberConfig: NumberConfigState) => {
  const msisdnOption = mapNumberConfigKeepNumberOption(
    numberConfig.keepNumberOption,
  );

  const ssnForTransferOwner = _determineOwnerSocialSecurityNumber(
    msisdnOption,
    numberConfig,
  );

  const currentPhoneNumber = _shouldIncludeCurrentPhoneNumber(msisdnOption)
    ? numberConfig.keepNumberInputValue
    : undefined;

  return {
    ShoppingCartItemId: numberConfig.cartItemId,
    MsisdnOption: msisdnOption,
    CurrentPhoneNumber: currentPhoneNumber,
    OwnerSocialSecurityNumber: ssnForTransferOwner,
    HiddenNumber: numberConfig.hiddenNumber,
    ESim: numberConfig.esim,
  };
};

const _createMainPlanData = (
  numberConfig: NumberConfigState,
): ConfirmOrderMainPlan => {
  const msisdnOption = mapNumberConfigKeepNumberOption(
    numberConfig.keepNumberOption,
  );

  const currentPhoneNumber = _shouldIncludeCurrentPhoneNumber(msisdnOption)
    ? numberConfig.keepNumberInputValue
    : undefined;

  return {
    ShoppingCartItemId: numberConfig.cartItemId,
    MsisdnOption: msisdnOption,
    CurrentPhoneNumber: currentPhoneNumber,
    HiddenNumber: numberConfig.hiddenNumber,
    ESim: numberConfig.esim,
    ChildPlans: [],
  };
};

const _createMainPlanForExistingCustomer = (
  currentPhoneNumber: string,
  childPlans: ConfirmOrderChildPlan[],
): ConfirmOrderMainPlan => {
  return {
    MsisdnOption: 'Empty',
    CurrentPhoneNumber: currentPhoneNumber,
    ChildPlans: childPlans,
  };
};

export const createConfirmOrderPayload = (
  customerConsents: CustomerConsentDto[],
  userState: UserState,
  fetchedData: FetchedData,
): HalebopConfirmOrderRequest => {
  const {
    numberConfigMap,
    selectedDeliveryMethod,
    inputtedInstaboxPhoneNumber,
    payment: {
      selectedDownPayTime,
      selectedPaymentMethodCommaSeparatedIdentifier,
    },
  } = userState;

  const {
    cart: { cartItems },
  } = fetchedData;

  const numberConfigKeys = Object.keys(numberConfigMap);

  const mainPlans: ConfirmOrderMainPlan[] = []; // customer can only order one but api expects an array
  const familyConnectionPlans: ConfirmOrderChildPlan[] = [];
  let parentMsisdn: string | undefined = undefined;

  numberConfigKeys.forEach((key) => {
    const numberConfig = numberConfigMap[key];
    if (!numberConfig) {
      return;
    }

    const cartItemId = numberConfig.cartItemId;
    const cartItem =
      cartItems.find((item) => item.Id === cartItemId) ||
      cartItems.map((x) => x.BundlePlanItem).find((y) => y?.Id === cartItemId);

    if (!cartItem) {
      return;
    }

    if (!cartItem.IsFamilyPlan) {
      mainPlans.push(_createMainPlanData(numberConfig));
    } else if (cartItem.IsFamilyPlan === true) {
      familyConnectionPlans.push(_createChildPlanData(numberConfig));
      parentMsisdn = cartItem.ParentMsisdn;
    }
  });

  const hasMainPlan = mainPlans.length > 0;
  const hasFamilyConnectionPlans = familyConnectionPlans.length > 0;

  if (hasMainPlan && hasFamilyConnectionPlans) {
    familyConnectionPlans.forEach((connection) => {
      mainPlans[0].ChildPlans.push(connection);
    });
  } else if (!hasMainPlan && hasFamilyConnectionPlans && parentMsisdn) {
    const mainPlan = _createMainPlanForExistingCustomer(
      parentMsisdn,
      familyConnectionPlans,
    );

    mainPlans.push(mainPlan);
  }

  const checkedCustomerConsents = customerConsents.filter(
    (consent: CustomerConsentDto) => consent.Checked === true,
  );

  const servicePoint = selectedDeliveryMethod?.ServicePoints?.find(
    (servicePoint) => servicePoint.Selected === true,
  );

  const pickupLocationKey = servicePoint?.Id;
  const pickupLocationCity = servicePoint?.Address?.City;
  const pickupLocationStreet = servicePoint?.Address?.Street;
  const pickupLocationZipCode = servicePoint?.Address?.Zip;

  const instaboxPhoneNumber =
    selectedDeliveryMethod?.ShippingMethodSystemName === INSTABOX_SYSTEM_NAME
      ? inputtedInstaboxPhoneNumber
      : undefined;

  const deliveryInfo: DeliveryInfo = {
    ContactPhoneNumber: instaboxPhoneNumber,
    PickupLocation: pickupLocationKey ?? '',
  };

  if (pickupLocationStreet && pickupLocationZipCode && pickupLocationCity) {
    deliveryInfo.PickupAddress = {
      City: pickupLocationCity,
      StreetAddress: pickupLocationStreet,
      ZipCode: pickupLocationZipCode,
    };
  }

  // If user selected a Klarna payment method, we want to calculate the
  // price as if it's a direct price, in order to display the full
  // amount and say it is to be paid however the user wants via Klarna
  let selectedDownPaymentToSend = selectedDownPayTime;
  if (
    selectedPaymentMethodCommaSeparatedIdentifier
      ?.toLowerCase()
      .includes('klarna')
  ) {
    selectedDownPaymentToSend = 1;
  }

  return {
    CustomerConsents: checkedCustomerConsents.map(
      (consent: CustomerConsentDto) => ({
        Purpose: consent.Purpose,
        Status: consent.Status,
      }),
    ),
    MainPlans: mainPlans,
    DeliveryInfo: {
      ContactPhoneNumber: instaboxPhoneNumber,
      PickupLocation: pickupLocationKey ?? '',
      PickupAddress: deliveryInfo.PickupAddress,
    },
    SelectedDownPayment: selectedDownPaymentToSend,
  };
};
